import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { useAuth, type Me } from '../lib/auth'
import { LogoIcon } from '../components/Icons'
import { ThemeToggle } from '../components/ThemeToggle'

/**
 * Anmeldung. Solange noch kein Konto existiert, zeigt dieselbe Seite die
 * Erstanlage – danach ist dieser Weg serverseitig gesperrt.
 */
export function LoginPage() {
  const auth = useAuth()
  const setupState = useQuery({
    queryKey: ['setup-state'],
    queryFn: ({ signal }) => apiGet<{ needs_setup: boolean }>('/setup/state', signal),
    retry: false,
  })

  if (setupState.isPending) {
    return <Centered>Wird geladen …</Centered>
  }
  return setupState.data?.needs_setup ? (
    <SetupForm onDone={() => auth.refresh()} />
  ) : (
    <SignInForm onDone={() => auth.refresh()} />
  )
}

export function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ width: '100%', maxWidth: 420 }}>{children}</div>
    </div>
  )
}

export function Brand() {
  return (
    <div className="row" style={{ justifyContent: 'space-between', marginBottom: 20 }}>
      <div className="row" style={{ gap: 10, fontSize: 20, fontWeight: 700 }}>
        <LogoIcon size={28} />
        Netzwarte
      </div>
      <ThemeToggle />
    </div>
  )
}

function SignInForm({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totp, setTOTP] = useState('')

  // Kein Vorab-Abgleich der Adresse: Er würde verraten, ob ein Konto
  // existiert. Das Code-Feld steht immer da; wer die Zwei-Faktor-Anmeldung
  // noch nicht eingerichtet hat, lässt es leer.
  const login = useMutation({
    mutationFn: () =>
      apiSend<Me>('POST', '/login', {
        email: email.trim(),
        password,
        ...(totp ? { totp: totp.trim() } : {}),
      }),
    onSuccess: onDone,
  })

  return (
    <Centered>
      <Brand />
      <section className="card">
        <div className="card-head">
          <h1 className="card-title">Anmelden</h1>
        </div>
        <form
          className="card-body stack"
          onSubmit={(e) => {
            e.preventDefault()
            if (email.trim() && password) login.mutate()
          }}
        >
          <div className="field">
            <label htmlFor="login-email">E-Mail-Adresse</label>
            <input
              id="login-email"
              className="input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="login-password">Kennwort</label>
            <input
              id="login-password"
              className="input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="login-totp">Bestätigungscode aus der App</label>
            <input
              id="login-totp"
              className="input mono"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              value={totp}
              onChange={(e) => setTOTP(e.target.value)}
            />
            <span className="muted" style={{ fontSize: 13 }}>
              Leer lassen, wenn die Zwei-Faktor-Anmeldung noch nicht eingerichtet ist.
            </span>
          </div>

          {login.isError ? (
            <div role="alert" style={{ color: 'var(--crit)' }}>
              {(login.error as Error).message}
            </div>
          ) : null}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={login.isPending || !email.trim() || !password}
          >
            {login.isPending ? 'Wird geprüft …' : 'Anmelden'}
          </button>
        </form>
      </section>
    </Centered>
  )
}

function SetupForm({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')

  const setup = useMutation({
    mutationFn: () =>
      apiSend<unknown>('POST', '/setup', {
        email: email.trim(),
        name: name.trim(),
        password,
      }),
    onSuccess: async () => {
      // Direkt anmelden, damit der erste Login keine zweite Eingabe braucht.
      await apiSend<Me>('POST', '/login', { email: email.trim(), password })
      onDone()
    },
  })

  const mismatch = repeat !== '' && repeat !== password
  const complete = email.trim() !== '' && name.trim() !== '' && password.length >= 12 && !mismatch

  return (
    <Centered>
      <Brand />
      <section className="card">
        <div className="card-head">
          <h1 className="card-title">Erstes Konto anlegen</h1>
        </div>
        <form
          className="card-body stack"
          onSubmit={(e) => {
            e.preventDefault()
            if (complete) setup.mutate()
          }}
        >
          <p className="muted" style={{ margin: 0 }}>
            Dieses Konto bekommt alle Rechte. Weitere Benutzer entstehen danach unter
            Einstellungen. Nach der Erstanlage ist dieser Weg gesperrt.
          </p>
          <div className="field">
            <label htmlFor="setup-name">Name</label>
            <input
              id="setup-name"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="setup-email">E-Mail-Adresse</label>
            <input
              id="setup-email"
              className="input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="setup-password">Kennwort (mindestens 12 Zeichen)</label>
            <input
              id="setup-password"
              className="input"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="setup-repeat">Kennwort wiederholen</label>
            <input
              id="setup-repeat"
              className="input"
              type="password"
              autoComplete="new-password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
            />
            {mismatch ? (
              <span style={{ color: 'var(--crit)', fontSize: 13 }}>
                Die beiden Eingaben stimmen nicht überein.
              </span>
            ) : null}
          </div>

          {setup.isError ? (
            <div role="alert" style={{ color: 'var(--crit)' }}>
              {(setup.error as Error).message}
            </div>
          ) : null}

          <button type="submit" className="btn btn-primary" disabled={setup.isPending || !complete}>
            {setup.isPending ? 'Wird angelegt …' : 'Konto anlegen'}
          </button>
        </form>
      </section>
    </Centered>
  )
}
